import React, { useState } from 'react';
import { EditableInput } from '@ordermentum/ui-shared/atoms/Field';
import { Row, Cell } from '@ordermentum/ui-shared/atoms/Grid';
import Button from '@ordermentum/ui-shared/atoms/Button';
import { useFormik, validateYupSchema } from 'formik';
import isEmpty from 'lodash/isEmpty';
import * as yup from 'yup';
import Spinner from '@ordermentum/ui-shared/atoms/Spinner';
import { errorComponent } from '@ordermentum/ui-shared/molecules/FormErrorComponent';
import { Location } from '@ordermentum/ui-definitions';
import { TrackEvent } from '../../Analytics/redux/actions';
import { Container, CenterContainer } from './Form.style';

const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup
    .string()
    .email('Email must be a valid')
    .required('Email address is required'),
  phone: yup
    .string()
    .required('Phone number is required')
    .max(50, 'Phone number is not valid'),
});

type Values = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type Props = {
  location: Location;
  userId?: string;
  trackEvent: TrackEvent;
  onSubmit: (values: Values) => Promise<void>;
};

const UserForm = ({ location, trackEvent, onSubmit }: Props) => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    validate: async values => {
      try {
        await validateYupSchema(values, schema);
      } catch (errors) {
        const err = errors.inner.reduce((acc, value) => {
          acc[value.path] = value.message;
          return acc;
        }, {});
        formik.setErrors(err);
        return err;
      }
      return {};
    },
    onSubmit: async values => {
      if (!isEmpty(formik.errors)) {
        return;
      }
      setLoading(true);
      try {
        await onSubmit(values);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        formik.setStatus({
          msg: 'Error in creating an account',
        });
        setTimeout(() => {
          formik.setStatus({});
        }, 3000);
      }
    },
  });
  const {
    values: { firstName, lastName, email, phone },
    errors,
    setFieldValue,
    handleSubmit,
    status,
    touched,
  } = formik;
  return (
    <Container>
      <form onSubmit={handleSubmit}>
        {errorComponent({}, status)}
        <Row>
          <Cell>
            <EditableInput
              autoFocus
              required
              labelWidth="50%"
              errors={errors}
              touched={touched}
              label="First name"
              tag="firstName"
              value={firstName}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableInput
              required
              labelWidth="50%"
              errors={errors}
              touched={touched}
              label="Last name"
              tag="lastName"
              value={lastName}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableInput
              required
              labelWidth="50%"
              errors={errors}
              touched={touched}
              label="Email address:"
              tag="email"
              value={email}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <Row>
          <Cell>
            <EditableInput
              required
              label="Phone number:"
              tag="phone"
              labelWidth="50%"
              value={phone}
              errors={errors}
              touched={touched}
              onChange={setFieldValue}
            />
          </Cell>
        </Row>
        <CenterContainer>
          <Button size="large" type="primary-submit">
            {loading ? (
              <Spinner color="white" size="sm" />
            ) : (
              'Create an account'
            )}
          </Button>
        </CenterContainer>
      </form>
    </Container>
  );
};

export { UserForm };
